/**
 * Frame3_2 - Assets Summary
 *
 * Purpose: High-level overview of estate assets
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DollarSign, Home, Car, Package, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/Button';

export default function Frame3_2() {
  const navigate = useNavigate();
  const { estateId } = useParams();

  const handleNext = () => {
    navigate(estateId ? `/frame-3-2-1/${estateId}` : '/frame-3-2-1');
  };

  const handleBack = () => {
    navigate(estateId ? `/frame-3-11/${estateId}` : '/frame-3-11');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Asset Categories</h1>
          <p className="text-gray-600">
            Overview of assets included in the estate plan
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-green-100 rounded-full p-3">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Financial Assets</h2>
            </div>
            <p className="text-gray-600 text-sm mb-3">
              Bank accounts, investments, retirement funds, life insurance policies, and other
              financial assets
            </p>
            <div className="text-sm text-cyan-600 font-medium">View details in next section</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-blue-100 rounded-full p-3">
                <Home className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Real Estate</h2>
            </div>
            <p className="text-gray-600 text-sm mb-3">
              Primary residence, vacation properties, rental properties, and land holdings
            </p>
            <div className="text-sm text-cyan-600 font-medium">View details in next section</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-orange-100 rounded-full p-3">
                <Car className="w-6 h-6 text-orange-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Vehicles</h2>
            </div>
            <p className="text-gray-600 text-sm mb-3">
              Cars, trucks, motorcycles, boats, RVs, and other titled vehicles
            </p>
            <div className="text-sm text-cyan-600 font-medium">View details in next section</div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-purple-100 rounded-full p-3">
                <Package className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Personal Property</h2>
            </div>
            <p className="text-gray-600 text-sm mb-3">
              Household items, jewelry, art, collectibles, digital assets, and business interests
            </p>
            <div className="text-sm text-cyan-600 font-medium">View details in next section</div>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button onClick={handleNext}>
            View Detailed Assets
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
